import * as React from 'react';
import styled from 'styled-components';
import {
  Tabs,
  TabPane,
  Text,
  InfiniteTableProps,
  theme,
  Link,
  Popconfirm,
} from 'components';

import {
  ListContactType,
  IGenericContact,
  ContactListSortOrder,
} from '@bb/contacts';

import { OrgDTO } from '@bb/domain/dist/types/dto/UserController/org';
import { AuthContext } from 'contexts';
import ClientContactTable from './ContactTables/ClientTable';
import SupplierContactTable from './ContactTables/SupplierTable';
import BrokerageContactTable from './ContactTables/BrokerageTable';
import ArchivedContactTable from './ContactTables/ArchivedTable';
import { TAB_TITLE_MAP } from './constants';

export interface ContactsTabsProps {
  onFilterSortChange: InfiniteTableProps['onChange'];
  loadMoreItems: InfiniteTableProps['loadMoreItems'];
  itemCount: number;
  data: (IGenericContact | OrgDTO['users'][number])[];
  sort?: {
    field: string;
    order: ContactListSortOrder;
  };
  filter?: Record<string, string[]>;
  onTabChange: (activeKey: string) => void;
  activeTab: ListContactType | 'brokerage';
  selectedCount: number;
  onSelect: InfiniteTableProps['onSelect'];
  loading: InfiniteTableProps['loading'];
  onRowClick: InfiniteTableProps['onItemClick'];
  searchCounts?: Record<ListContactType | 'brokerage', number>;
  onDelete: () => void;
  onRestore: (contactId: string) => void;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
}

const ContactsTabs: React.FC<ContactsTabsProps> = ({
  onTabChange,
  activeTab,
  itemCount,
  data,
  sort,
  filter,
  loadMoreItems,
  onFilterSortChange,
  selectedCount,
  searchCounts,
  onSelect,
  onRowClick,
  onDelete,
  onRestore,
  loading,
  page,
  setPage,
}) => {
  const { hasFeature } = React.useContext(AuthContext);
  if (!hasFeature('MY_BROKERAGE')) {
    delete TAB_TITLE_MAP.brokerage;
  }

  const renderExtraContent = () =>
    loading ? (
      <StyledResultCounter>Loading results...</StyledResultCounter>
    ) : (
      <>
        {!!selectedCount && (
          <Popconfirm
            width={250}
            title="Deleting these contacts will remove their profiles across the entire system including listing configurations."
            onConfirm={onDelete}
          >
            <StyledDeleteLink>Delete</StyledDeleteLink>
          </Popconfirm>
        )}
        {
          <StyledResultCounter>
            {selectedCount || itemCount}{' '}
            {selectedCount ? 'Selected' : 'Result(s)'}
          </StyledResultCounter>
        }
      </>
    );

  const tableProps = {
    loading,
    itemCount,
    loadMoreItems,
    onItemClick: onRowClick,
    onChange: onFilterSortChange,
    dataSource: data,
    sort,
    filter,
    rowHeight: () => 80,
    page,
    setPage,
  };

  return (
    <StyledTabs
      activeKey={activeTab}
      tabBarGutter={32}
      onChange={onTabChange}
      tabBarExtraContent={renderExtraContent()}
    >
      {Object.keys(TAB_TITLE_MAP).map((KEY: keyof typeof TAB_TITLE_MAP) => (
        <StyledPane
          key={KEY}
          tab={
            <StyledTabTitle $active={activeTab === KEY}>
              {KEY === ListContactType.Archived && <Separator />}
              {TAB_TITLE_MAP[KEY]}
              {searchCounts && (
                <StyledSearchCount>{searchCounts[KEY]}</StyledSearchCount>
              )}
            </StyledTabTitle>
          }
        >
          {activeTab === ListContactType.Client && (
            <ClientContactTable {...tableProps} onSelect={onSelect} />
          )}
          {activeTab === ListContactType.Supplier && (
            <SupplierContactTable {...tableProps} onSelect={onSelect} />
          )}
          {activeTab === 'brokerage' && (
            <BrokerageContactTable {...tableProps} />
          )}
          {activeTab === ListContactType.Archived && (
            <ArchivedContactTable {...tableProps} onRestore={onRestore} />
          )}
        </StyledPane>
      ))}
    </StyledTabs>
  );
};

export default ContactsTabs;

const StyledTabTitle = styled(Text)<{ $active: boolean }>`
  padding: 16px 0;
  font-size: 100%;
  line-height: 24px;
  margin: 0;
  color: ${({ $active }) =>
    $active ? theme.getColor('textPrimary') : theme.getColor('textSecondary')};
`;

const Separator = styled.span`
  border-left: 1px solid ${theme.gray4};
  height: 30px;
  width: 1px;
  left: -16px;
  position: absolute;
`;

const StyledPane = styled(TabPane)`
  display: block;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  max-height: 100%;
  background-color: transparent;
`;

const StyledTabs = styled(Tabs).attrs({
  tabBarStyle: {
    padding: '0 16px',
    backgroundColor: theme.white,
    margin: 0,
  },
})`
  border-top: 1px solid ${theme.gray4};
  display: flex;
  flex-grow: 0;
`;

const StyledResultCounter = styled(Text)`
  color: ${theme.gray7};
  font-size: 16px;
  line-height: 24px;
  margin-left: 12px;
  margin-right: 12px;
`;

const StyledDeleteLink = styled(Link)`
  && {
    color: ${theme.red6};
  }
  &&:hover {
    color: ${theme.red3};
  }
  font-size: 16px;
  line-height: 24px;
  margin-left: 12px;
`;

const StyledSearchCount = styled.span`
  background-color: ${theme.gray3};
  color: ${theme.gray8};
  font-size: 12px;
  line-height: 20px;
  font-weight: 600;
  width: 24px;
  height: 20px;
  padding: 3px 8px;
  border-radius: 16px;
  margin-left: 10px;
`;
